import { array, date, MixedSchema, object, ref, string } from "yup";
import { FormikValues } from "formik";
import { PaymentPreference } from "../component/enums/PaymentPreference";
import { UploadedDocument } from "../component/domain/UploadedDocument";
import { DateTime } from "luxon";
import { DocumentType } from "../component/enums/DocumentType";
import { BeneficiaryDesignationStatus } from "../component/enums/BeneficiaryDesignationStatus";
import { Party } from "../component/enums/Party";
import _ from "lodash";
import { findDocumentTypesFor, hasRequiredDocumentsFor } from "../component/enums/BenefitType";
import { FormValues } from "../component/common/FormValues";
import { Illness } from "../component/enums/Illness";

export function requiredIfFieldNotPresent(field: string, message: string, schema: MixedSchema<any | undefined> = string()) {
    return schema.when(field, {
        is: (value) => value !== undefined && value.length > 0,
        then: schema.notRequired(),
        otherwise: schema.required(message)
    });
}

export function requiredIfFieldNotChecked(field: string, message: string, schema: MixedSchema<any | undefined> = string()) {
    return schema.when(field, {
        is: (value) => value === true,
        then: schema.notRequired(),
        otherwise: schema.required(message)
    });
}

export function dateRangeValidator(startDateField: string) {
    return date()
        .typeError("You must provide a valid date.")
        .when(
            startDateField,
            (startDate: any, schema: any) => {
                try {
                    return (startDate && schema
                        .min(startDate, "The end date must be greater than the start date."))
                } catch(e) {
                    return schema.typeError("You must provide a valid date.")
                }
            }
        )
}

export function lessThanEqualToCurrentDateValidator() {
    const currentDate = DateTime.now().toLocaleString();
    return date()
        .typeError("You must provide a valid date.")
        .max(currentDate, "The inputted date must be less than or equal to the current date.")
}

export function validRoutingNumber(party: Party) {
    return string().when(findFieldNameFor(party, 'paymentPreference'), {
        is: (value) => {
            return value !== undefined && value === PaymentPreference.EFT;
        },
        then: string().test(
            'valid-routing-number',
            'Please enter a valid routing number.',
            ((value: any) => {
                if (!value || !/^[0-9]{9}$/.test(value.toString())) {
                    return false;
                }
                let sum = 0;
                for (let i = 0; i < value.length; i += 3) {
                    sum += parseInt(value.charAt(i)) * 3
                        + parseInt(value.charAt(i + 1)) * 7
                        + parseInt(value.charAt(i + 2));
                }
                return !(sum === 0 || sum % 10 !== 0);
            }),
        )
    })
}

export function accountNumberValidator(party: Party) {
    return string().oneOf([ref(findFieldNameFor(party, 'accountNumber'))], 'The account numbers must match.');
}

export function validateDocumentContent() {
    return array().of(
        object().shape({
            documentContent: string()
                .test('type', 'File is not a PDF.', val => val?.startsWith('data:application/pdf') ?? true)
                .notRequired()
        })
    );
}

export function validateDocumentsTotalSize(values: FormikValues) {
    const documents = values.documents as UploadedDocument[];
    const totalLength = documents.reduce<number>((accumulator, current) => {
        return accumulator + (current.documentContent?.length ?? 0);
    }, 0);
    if (totalLength > 4100000) {
        return {
            documents: 'Files are too large (total must be less than 3MB).'
        }
    }
    return {}
}

export function validateBeneficiaryDesignation(values: FormikValues) {
    const notProvidedBeneficiary = values.beneficiaryDesignationStatus === BeneficiaryDesignationStatus.UPLOADED
        && !values.documents.some((d: UploadedDocument) =>
            [DocumentType.BENEFICIARY_DESIGNATION, DocumentType.ENROLLMENT_FORM].includes(d.documentType as DocumentType) && d.documentContent)
    if (notProvidedBeneficiary) {
        return {
            beneficiaryDesignationStatus: "You must either provide a beneficiary designation or state that you don't have one."
        }
    }
    return {}
}

export function validatePolicyNumber() {
    return validateStringLength('policy-number-length', 'Account number must be 7 digits.', 7);
}

export function validateUnitNumber() {
    return validateStringLength('unit-number-length', 'Bill group number must be 5 digits.', 5);
}

export function validatePrivacyId() {
    const errorMessage = 'Principal member ID must be 9 digits and start with a 9.';
    return validateStringLength('policy-number-length', errorMessage, 9)
        .test('policy-number-starts-with-9', errorMessage, val => !val || val.length === 0 || val.startsWith('9'));
}

export function validateInfertility(values: FormikValues) {
    const didNotUndergoProcedure = values.medicalAssistanceWithPregnancy === "No"
        && values.illnesses?.some((i: Illness) => i.startsWith("Infertility"))
    if (didNotUndergoProcedure) {
        return {
            illnesses: `Medical evidence of a diagnosis of infertility and surgical treatment by a physician is required in order to file for Infertility.`
        }
    }
    return {}
}

export function validateStringLength(testName: string, errorMessage: string, length: number) {
    return string().test(testName, errorMessage, val => !val || val.length === 0 || val.length === length);
}

export function validateFieldNotEqualTo(fieldToCompare: string, errorMessage: string) {
    return string().when(fieldToCompare, (value: any, schema: any) => {
        return (value && schema.notOneOf([value], errorMessage))
    })
}

export function validateTwoFieldsNotEqualTo(fieldOneToCompare: string, fieldTwoToCompare: string, errorMessage: string) {
    return string().when([fieldOneToCompare, fieldTwoToCompare], (valueOneToCompare: any, valueTwoToCompare: any, schema: any) => {
        const value = valueOneToCompare + ` ${valueTwoToCompare}`;
        return (value && schema.notOneOf([value], errorMessage))
    })
}

export function validateHasRequiredDocuments(values: FormikValues) {
    const notProvidedDocument = hasRequiredDocumentsFor(values as FormValues) && !values.documents.some((d: UploadedDocument) =>
            findDocumentTypesFor(values as FormValues).includes(d.documentType as DocumentType) && d.documentContent)
    if (notProvidedDocument) {
        return {
            documents: "You must provide at least one required document."
        }
    }
    return {}
}

function findFieldNameFor(party: Party, fieldName: string) {
    return _.camelCase(Party.EMPLOYER === party ? `employer-${fieldName}` : fieldName);
}
